import { useEffect, useState } from "react";
import {
	Badge,
	Box,
	Container,
	Grid,
	GridItem,
	Heading,
	Image,
	Stack,
	Text,
} from "@chakra-ui/react";
import { useCmsApi } from "../../services/useCmsApi";

interface ISeasonCard {
	timing: string;
	color: string;
	description: string;
	image: string;
	badge?: string;
	imageFit?: "cover" | "contain";
	imageBg?: string;
}

const defaultSeasons: ISeasonCard[] = [
	{
		timing: "Pre-Emerge",
		color: "yellow",
		description: "Target residual herbicides before weeds have emerged.",
		image: "/season-pre-emerge-equipment.jpg",
	},
	{
		timing: "Planting",
		color: "green",
		description: "Optimize seeding rates in weedy zones.",
		image: "/season-planting-seeding.jpg",
	},
	{
		timing: "In-Crop",
		color: "teal",
		description: "Increase spray intensity in weed patches.",
		image: "/season-in-crop-sprayer.jpg",
	},
	{
		timing: "Harvest",
		color: "orange",
		description: "Focus harvest weed seed control where it is needed most.",
		badge: "Piloting Now",
		image: "/HARVEST_IMAGE.jpeg",
	},
];

const DEFAULT_SEASONAL_HEADING = "Target weed pressure where it matters most.";
const DEFAULT_SEASONAL_OUTCOMES = "Geco helps North American crop farmers prioritize action across every field, directing inputs and effort to the areas that drive outcomes.";
const DEFAULT_SEASONAL_OUTCOMES_2 = "Reduce weed seedbanks in problem zones and improve field productivity using prescriptions compatible with your existing equipment.";

export const SeasonalUseSection = (): JSX.Element => {
	const cmsApi = useCmsApi();
	const [seasons, setSeasons] = useState<ISeasonCard[]>(defaultSeasons);
	const [heading, setHeading] = useState(DEFAULT_SEASONAL_HEADING);

	useEffect(() => {
		cmsApi.getSiteSetting("seasonalUseHeading").then((s) => { if (s?.value) setHeading(s.value); });
		cmsApi.getSiteSetting("seasonCards").then((s) => {
			if (s?.value) {
				try {
					const parsed = JSON.parse(s.value) as ISeasonCard[];
					if (Array.isArray(parsed) && parsed.length > 0) setSeasons(parsed);
				} catch { /* fallback to defaults */ }
			}
		});
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	return (
	<Box py={{ base: "16", md: "24" }} bg="gray.50">
		<Container maxW="1480px">
			<Stack spacing="12">
				<Stack spacing="3" textAlign="center" align="center">
					<Heading size={{ base: "sm", md: "xl" }} color="gray.800">
						{heading}
					</Heading>
					<Text color="gray.600" fontSize={{ base: "lg", md: "xl" }} maxW="2xl" mx="auto">
						{DEFAULT_SEASONAL_OUTCOMES}
					</Text>
					<Text color="gray.600" fontSize={{ base: "lg", md: "xl" }} maxW="2xl" mx="auto">
						{DEFAULT_SEASONAL_OUTCOMES_2}
					</Text>
				</Stack>
				<Image
					src="/case-studies/case1/Heinrich%20Out%20West_spray_map_options.png"
					loading="lazy"
					alt="Prescription map showing targeted weed zones"
					maxW={{ base: "280px", md: "420px" }}
					w="full"
					mx="auto"
					display="block"
					borderRadius="lg"
					border="1px solid"
					borderColor="gray.200"
					bg="white"
					p="2"
				/>
				<Grid
					templateColumns={{
						base: "1fr",
						sm: "repeat(2, 1fr)",
						lg: "repeat(4, 1fr)",
					}}
					gap="4"
					alignItems="stretch"
				>
					{seasons.map((s) => (
						<GridItem key={s.timing}>
							<Stack
								h="full"
								bg="white"
								borderRadius="lg"
								border="1px solid"
								borderColor="gray.200"
								borderTop="4px solid"
								borderTopColor={`${s.color}.400`}
								spacing="0"
								overflow="hidden"
								justify="space-between"
							>
								<Image
									src={s.image}
									alt={s.timing}
									h={{ base: "180px", lg: "190px" }}
									w="full"
									objectFit={s.imageFit || "cover"}
									objectPosition="center"
									bg={s.imageBg || "transparent"}
									loading="lazy"
								/>
								<Stack spacing="3" p="5" flex="1" justify="center">
									<Heading size="sm" color="gray.800" minH={{ lg: "52px" }}>
										{s.timing}
										{s.badge && (
											<Badge ml="2" colorScheme="orange" fontSize="xs">
												{s.badge}
											</Badge>
										)}
									</Heading>
									<Text color="gray.600" fontSize="sm" minH={{ lg: "64px" }}>
										{s.description}
									</Text>
								</Stack>
							</Stack>
						</GridItem>
					))}
				</Grid>
				<Box
					maxW="980px"
					mx="auto"
					w="full"
					bg="white"
					borderRadius="lg"
					border="1px solid"
					borderColor="blue.200"
					p={{ base: "5", md: "6" }}
					textAlign="center"
				>
					<Heading size="md" color="gray.800">
						No new equipment. No new practices. Results in 1 day.
					</Heading>
				</Box>
			</Stack>
		</Container>
	</Box>
	);
};
